import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  FirstAidKit, Package, Clock, User, MagnifyingGlass, Wrench, ArrowRight,
  ClipboardText, CheckCircle, SpeakerHigh, SpeakerSlash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AccessoryChips from "@/components/AccessoryChips";

// Colores por origen — deben ser consistentes con Hospital.jsx y Patients.jsx.
const PRIORITY_META = {
  urgent: { label: "URGENTE", color: "#E0000F", bg: "#fee2e2" },
  high:   { label: "ALTA",    color: "#d97706", bg: "#fef3c7" },
  normal: { label: "NORMAL",  color: "#1E88E5", bg: "#dbeafe" },
  low:    { label: "BAJA",    color: "#52525B", bg: "#f4f4f5" },
};

const TICKET_STATUS_LABEL = {
  pendiente_diagnostico:  "Pendiente diagnóstico",
  en_diagnostico:         "En diagnóstico",
  pendiente_refaccion:    "Pendiente refacción",
  en_espera_autorizacion: "Espera autorización",
  enviado_externo:        "Con técnico externo",
};

const PATIENT_STATE_LABEL = {
  recepcion:             "Recepción",
  para_pedidos:          "Para pedidos",
  esperando_refacciones: "Esperando refacciones",
  segunda_revision:      "Segunda revisión",
  taller_externo:        "Taller externo",
};

function daysBetween(iso) {
  if (!iso) return 0;
  const then = new Date(iso).getTime();
  return Math.max(0, Math.floor((Date.now() - then) / (1000 * 60 * 60 * 24)));
}

export default function MyTechWork() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [origin, setOrigin] = useState("all"); // all | taller | hospital
  const [search, setSearch] = useState("");
  const [alertMuted, setAlertMuted] = useState(() => {
    try { return localStorage.getItem("mytech_alert_muted") === "1"; } catch { return false; }
  });
  const knownIdsRef = useRef(null); // Set<string> of `t:<id>`/`p:<id>`; null pre-first-load
  const audioCtxRef = useRef(null);

  const playPriorityBeep = () => {
    if (alertMuted) return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      [
        { f: 880, t: now, d: 0.18 },
        { f: 1320, t: now + 0.22, d: 0.28 },
      ].forEach(({ f, t, d }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(f, t);
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.35, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + d);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + d + 0.05);
      });
    } catch { /* ignore */ }
  };

  const toggleMute = () => {
    setAlertMuted((prev) => {
      const next = !prev;
      try { localStorage.setItem("mytech_alert_muted", next ? "1" : "0"); } catch { /* ignore */ }
      if (!next) {
        setTimeout(() => {
          try {
            if (!audioCtxRef.current) {
              const Ctx = window.AudioContext || window.webkitAudioContext;
              audioCtxRef.current = new Ctx();
            }
            const ctx = audioCtxRef.current;
            if (ctx.state === "suspended") ctx.resume().catch(() => {});
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(1320, ctx.currentTime);
            gain.gain.setValueAtTime(0.0001, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.2);
            osc.connect(gain).connect(ctx.destination);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 0.25);
          } catch { /* ignore */ }
        }, 40);
      }
      return next;
    });
  };

  const load = async () => {
    try {
      const { data } = await api.get("/technician/my-work");
      const nextTickets = data.tickets || [];
      const nextPatients = data.patients || [];
      // Detección de items nuevos → beep + toast.
      const prev = knownIdsRef.current;
      const nextIds = new Set([
        ...nextTickets.map((x) => `t:${x.id}`),
        ...nextPatients.map((x) => `p:${x.id}`),
      ]);
      if (prev !== null) {
        const newTickets = nextTickets.filter((x) => !prev.has(`t:${x.id}`));
        const newPatients = nextPatients.filter((x) => !prev.has(`p:${x.id}`));
        const total = newTickets.length + newPatients.length;
        if (total > 0) {
          playPriorityBeep();
          const first = newTickets[0] || newPatients[0];
          const title = newTickets[0] ? (newTickets[0].product_name || "Nuevo ticket")
                                       : `${newPatients[0].brand || ""} ${newPatients[0].model || ""}`.trim();
          toast(`Nueva asignación · ${newTickets[0] ? "Taller" : "Hospital"}`, {
            description: `${title}${total > 1 ? ` y ${total - 1} más` : ""}`,
            duration: 6000,
          });
        }
      }
      knownIdsRef.current = nextIds;
      setTickets(nextTickets);
      setPatients(nextPatients);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const int = setInterval(load, 20000);
    return () => clearInterval(int);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Normaliza tickets + patients a un formato común para render.
  const items = useMemo(() => {
    const t = tickets.map((x) => ({
      _key: `t:${x.id}`,
      origin: "taller",
      id: x.id,
      title: x.product_name,
      subtitle: x.fault_description || x.reason,
      state: x.status,
      stateLabel: TICKET_STATUS_LABEL[x.status] || x.status,
      priority: x.priority,
      claimedAt: x.claimed_at,
      days: daysBetween(x.claimed_at || x.created_at),
      labels: x.labels || [],
      source: x.source, // piso_de_venta / cliente
      device: x.client_device || null,
      href: "/hospital",
      raw: x,
    }));
    const p = patients.map((x) => ({
      _key: `p:${x.id}`,
      origin: "hospital",
      id: x.id,
      device: x,
      title: `${x.brand || ""} ${x.model || ""}`.trim() || x.code,
      subtitle: (x.issue || "").trim() || (x.observations || "").trim() || [x.processor, x.ram, x.storage].filter(Boolean).join(" · ") || x.code,
      state: x.current_state,
      stateLabel: PATIENT_STATE_LABEL[x.current_state] || x.current_state,
      priority: null,
      claimedAt: x.claimed_at,
      days: daysBetween(x.claimed_at || x.received_at || x.created_at),
      labels: x.labels || [],
      source: null,
      href: "/hospital-equipos",
      raw: x,
    }));
    let all = [...t, ...p];
    if (origin === "taller") all = all.filter((i) => i.origin === "taller");
    if (origin === "hospital") all = all.filter((i) => i.origin === "hospital");
    const q = search.trim().toLowerCase();
    if (q) {
      const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const tokens = norm(q).split(/\s+/).filter(Boolean);
      all = all.filter((i) => {
        const hay = `${norm(i.title)} ${norm(i.subtitle)} ${norm(i.stateLabel)} ${(i.labels || []).map((l) => norm(l.text)).join(" ")}`;
        return tokens.every((tok) => hay.includes(tok));
      });
    }
    // Ordena por prioridad urgente → luego más antiguos primero.
    const prOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
    all.sort((a, b) => {
      const pa = prOrder[a.priority] ?? 2;
      const pb = prOrder[b.priority] ?? 2;
      if (pa !== pb) return pa - pb;
      return b.days - a.days;
    });
    return all;
  }, [tickets, patients, origin, search]);

  const counts = useMemo(() => ({
    all: tickets.length + patients.length,
    taller: tickets.length,
    hospital: patients.length,
  }), [tickets.length, patients.length]);

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">MI TRABAJO · TÉCNICO</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight flex items-center gap-3">
            <Wrench size={30} weight="duotone" className="text-[#002FA7]" />
            Mi Tablero
          </h1>
          <p className="text-zinc-500 mt-1 text-sm max-w-2xl">
            Todo lo que has tomado en el Taller y el Hospital Interno, unificado en un solo lugar.
            Sólo verás tarjetas asignadas a <b>{user?.name || user?.email}</b>. Los pendientes por tomar no aparecen aquí.
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm text-zinc-600">
          <button
            type="button"
            onClick={toggleMute}
            title={alertMuted ? "Alertas silenciadas · click para activar" : "Alertas activas · click para silenciar"}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest px-3 py-2 border rounded-sm transition-colors ${alertMuted ? "border-zinc-200 text-zinc-400 bg-zinc-50 hover:border-zinc-300" : "border-[#00873D] text-[#00873D] bg-emerald-50 hover:bg-emerald-100"}`}
            data-testid="my-work-alert-toggle"
          >
            {alertMuted ? <SpeakerSlash size={13} weight="bold" /> : <SpeakerHigh size={13} weight="bold" />}
            {alertMuted ? "Alertas OFF" : "Alertas ON"}
          </button>
          <div className="text-right">
            <div className="font-mono-data text-[10px] uppercase text-zinc-500">Total</div>
            <div className="font-heading text-3xl font-extrabold text-[#002FA7]">{counts.all}</div>
          </div>
        </div>
      </header>

      {/* Origen tabs */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por producto, falla, especificaciones o etiqueta…"
            className="pl-9"
            data-testid="my-work-search"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {[
            { v: "all", label: "Todos", color: "#002FA7", count: counts.all },
            { v: "taller", label: "Taller y Reparación", color: "#E0000F", count: counts.taller, Icon: FirstAidKit },
            { v: "hospital", label: "Hospital Interno", color: "#7C3AED", count: counts.hospital, Icon: Package },
          ].map((tab) => (
            <button
              key={tab.v}
              onClick={() => setOrigin(tab.v)}
              data-testid={`origin-filter-${tab.v}`}
              className={`px-3 py-1.5 text-xs font-medium rounded-sm border transition-all inline-flex items-center gap-1.5 ${
                origin === tab.v ? "border-transparent text-white" : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400"
              }`}
              style={origin === tab.v ? { background: tab.color } : {}}
            >
              {tab.Icon && <tab.Icon size={12} weight="bold" />}
              {tab.label}
              <span className={`ml-1 text-[10px] font-mono-data ${origin === tab.v ? "bg-white/25 text-white" : "bg-zinc-100 text-zinc-600"} px-1.5 py-0.5 rounded-sm`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center text-zinc-400 py-16">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-200 rounded-md py-16 text-center">
          <CheckCircle size={40} weight="duotone" className="text-emerald-500 mx-auto" />
          <div className="mt-2 font-heading text-lg text-zinc-700">Sin trabajo asignado</div>
          <p className="text-sm text-zinc-500 max-w-md mx-auto mt-1">
            Cuando tomes una tarjeta del Taller o del Hospital Interno,
            aparecerá aquí para que la gestiones desde un solo lugar.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="my-work-grid">
          {items.map((it) => {
            const isTaller = it.origin === "taller";
            const prMeta = it.priority ? PRIORITY_META[it.priority] : null;
            return (
              <button
                key={it._key}
                onClick={() => {
                  const qs = it.origin === "taller"
                    ? `?ticket=${encodeURIComponent(it.id)}`
                    : `?patient=${encodeURIComponent(it.id)}`;
                  navigate(`${it.href}${qs}`);
                }}
                className="text-left w-full border border-zinc-200 bg-white rounded-sm p-3 hover:border-[#002FA7] hover:shadow-sm transition-all"
                data-testid={`my-work-item-${it.id}`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span
                    className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm text-white"
                    style={{ background: isTaller ? "#E0000F" : "#7C3AED" }}
                  >
                    {isTaller ? <FirstAidKit size={9} weight="bold" /> : <Package size={9} weight="bold" />}
                    {isTaller ? "Taller" : "Hospital"}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono-data text-zinc-500">
                    <Clock size={10} /> {it.days}d
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-1 mb-1">
                  {prMeta && (
                    <span
                      className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm"
                      style={{ background: prMeta.bg, color: prMeta.color }}
                    >
                      {prMeta.label}
                    </span>
                  )}
                  <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-600">
                    {it.stateLabel}
                  </span>
                  {isTaller && it.source === "cliente" && (
                    <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm text-white bg-[#E0000F]">
                      Máq. cliente
                    </span>
                  )}
                  {isTaller && it.source === "piso_de_venta" && (
                    <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm text-white bg-[#F97316]">
                      Piso
                    </span>
                  )}
                </div>
                <div className="font-semibold text-sm text-zinc-900 leading-snug line-clamp-2">{it.title || "—"}</div>
                <div className="text-[11px] text-zinc-500 italic mt-0.5 line-clamp-2">{it.subtitle || "—"}</div>
                {it.device && <AccessoryChips device={it.device} size="xs" className="mt-1.5" testid={`tech-acc-${it.id}`} />}
                {(it.labels || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {it.labels.slice(0, 4).map((lb, i) => (
                      <span
                        key={i}
                        className="inline-block text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm text-white"
                        style={{ background: lb.color || "#52525B" }}
                      >
                        {lb.text}
                      </span>
                    ))}
                    {it.labels.length > 4 && (
                      <span className="text-[9px] text-zinc-400 font-mono-data self-center">+{it.labels.length - 4}</span>
                    )}
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between text-[10px] text-zinc-500">
                  <span className="inline-flex items-center gap-1">
                    <User size={10} /> {it.claimedAt ? `Tomado ${new Date(it.claimedAt).toLocaleDateString("es-MX")}` : "—"}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[#002FA7] font-semibold">
                    Abrir <ArrowRight size={10} weight="bold" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
